import { useState, useEffect, useCallback } from "react";
import useGet from "../read/useGet";
import usePut from "../usePut";
import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { debounce } from "lodash";

const useGeofencing = () => {
  const queryClient = useQueryClient();
  const [pageNo, setPageNo] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddingData, setIsAddingData] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addLocationModal, setAddLocationModal] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [userManagementModalOpen, setUserManagementModalOpen] = useState(false);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);

  // Track added and removed users for the current session
  const [addedUsers, setAddedUsers] = useState([]);
  const [removedUsers, setRemovedUsers] = useState([]);

  // Local state for available and added users (for UI display only)
  const [localAvailableUsers, setLocalAvailableUsers] = useState([]);
  const [localAddedUsers, setLocalAddedUsers] = useState([]);

  // Pagination states for available and added users
  const [availableUsersPageNo, setAvailableUsersPageNo] = useState(0);
  const [addedUsersPageNo, setAddedUsersPageNo] = useState(0);

  // Search states for available and added users
  const [availableUsersSearchQuery, setAvailableUsersSearchQuery] =
    useState("");
  const [addedUsersSearchQuery, setAddedUsersSearchQuery] = useState("");

  // Initial data for listing
  const initialGeofencingData = {
    page_number: 0,
    limit: 10,
    search_query: "",
  };

  // Data fetching for geofencing listing
  const {
    list: geofenceList,
    paramsObject,
    setGetListParams,
  } = useGet({
    url: "geo_fencing/listing",
    method: "get",
    initialData: initialGeofencingData,
  });

  // Fetch single geofence location details
  const {
    list: singleGeofenceData,
    setGetListParams: setSingleGeofenceParams,
  } = useGet({
    url: "geo_fencing/fetch_single",
    method: "get",
    initialData: {
      branch_id: selectedBranchId,
    },
    enabled: !!selectedBranchId && isEditing,
  });

  // Fetch all users (available + selected) with search and pagination
  const { list: allUsersList, setGetListParams: setAllUsersParams } = useGet({
    url: "geo_fencing/fetch_all_users",
    method: "get",
    initialData: {
      branch_id: selectedBranchId,
      search_query: availableUsersSearchQuery,
      page_number: availableUsersPageNo,
      limit: 10,
    },
    enabled: !!selectedBranchId && userManagementModalOpen,
  });

  // Fetch already mapped users with search and pagination
  const { list: mappedUsersList, setGetListParams: setMappedUsersParams } =
    useGet({
      url: "geo_fencing/fetch_map_users",
      method: "get",
      initialData: {
        branch_id: selectedBranchId,
        search_query: addedUsersSearchQuery,
        page_number: addedUsersPageNo,
        limit: 10,
      },
      enabled: !!selectedBranchId && userManagementModalOpen,
    });

  // Create/Update a geofence location
  const { handleAuth: createUpdateGeofence, isLoading: isSubmitting } = usePut({
    url: "geo_fencing/create_and_update",
    refreshUrl: "geo_fencing/listing",
    onSuccess: () => {
      setIsAddingData(false);
      setIsEditing(false);
      setAddLocationModal(false);
      toast.success(
        isEditing
          ? "Location updated successfully"
          : "Location added successfully"
      );
    },
    onError: (error) => {
      toast.error("Failed to save location");
      setIsAddingData(false);
      console.error("Geofence creation/update error:", error);
    },
  });

  // Map users to geofence
  const { handleAuth: mapUsers, isLoading: isMapping } = usePut({
    url: "geo_fencing/map_users",
    refreshUrl: ["geo_fencing/fetch_all_users", "geo_fencing/fetch_map_users"],
    onSuccess: () => {
      toast.success("Users mapped successfully");
      setUserManagementModalOpen(false);
      // Reset added and removed users after successful mapping
      setAddedUsers([]);
      setRemovedUsers([]);
    },
    onError: (error) => {
      toast.error("Failed to map users");
      console.error("User mapping error:", error);
    },
  });

  // Delete a geofence location
  const { handleAuth: deleteGeofence, isLoading: isDeleteSubmitting } = usePut({
    url: "geo_fencing/delete",
    refreshUrl: "geo_fencing/listing",
    onSuccess: () => {
      setIsDeleting(false);
      setDeleteConfirmationModal(false);
      toast.success("Location deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete location");
      setIsDeleting(false);
      console.error("Geofence deletion error:", error);
    },
  });

  // Initialize local user lists when API data changes
  useEffect(() => {
    if (userManagementModalOpen && allUsersList?.data?.data) {
      // Set available users (those not already selected)
      const newUsers =
        allUsersList.data.data.filter((user) => !user.selected) || [];

      if (availableUsersPageNo === 0) {
        // For page 0, replace the entire list
        setLocalAvailableUsers(newUsers);
      } else {
        // For subsequent pages, append to existing list
        setLocalAvailableUsers((prev) => [...prev, ...newUsers]);
      }
    }
  }, [userManagementModalOpen, allUsersList?.data?.data, availableUsersPageNo]);

  useEffect(() => {
    if (userManagementModalOpen && mappedUsersList?.data?.data) {
      // Set added users with pagination support
      const newMappedUsers = mappedUsersList.data.data || [];

      if (addedUsersPageNo === 0) {
        // For page 0, replace the entire list
        setLocalAddedUsers(newMappedUsers);
      } else {
        // For subsequent pages, append to existing list
        setLocalAddedUsers((prev) => [...prev, ...newMappedUsers]);
      }
    }
  }, [userManagementModalOpen, mappedUsersList?.data?.data, addedUsersPageNo]);

  // Reset user tracking when branch changes or modal closes
  useEffect(() => {
    if (!userManagementModalOpen) {
      setAddedUsers([]);
      setRemovedUsers([]);
      setAvailableUsersSearchQuery("");
      setAddedUsersSearchQuery("");
      setLocalAvailableUsers([]);
      setLocalAddedUsers([]);
      setAvailableUsersPageNo(0);
      setAddedUsersPageNo(0);
    }
  }, [selectedBranchId, userManagementModalOpen]);

  // Debounced search handlers with dependencies for proper memoization
  const debouncedAvailableUsersSearch = useCallback(
    debounce((query) => {
      if (selectedBranchId && userManagementModalOpen) {
        // Reset page when search query changes
        setAvailableUsersPageNo(0);
        setAllUsersParams({
          branch_id: selectedBranchId,
          search_query: query,
          page_number: 0, // Reset to first page when searching
          limit: 10,
        });
      }
    }, 500),
    [selectedBranchId, userManagementModalOpen, setAllUsersParams]
  );

  const debouncedAddedUsersSearch = useCallback(
    debounce((query) => {
      if (selectedBranchId && userManagementModalOpen) {
        // Reset page when search query changes
        setAddedUsersPageNo(0);
        setMappedUsersParams({
          branch_id: selectedBranchId,
          search_query: query,
          page_number: 0, // Reset to first page when searching
          limit: 10,
        });
      }
    }, 500),
    [selectedBranchId, userManagementModalOpen, setMappedUsersParams]
  );

  // Handler for available users search input - memoize to create stable references
  const handleAvailableUsersSearch = useCallback(
    (query) => {
      setAvailableUsersSearchQuery(query);
      debouncedAvailableUsersSearch(query);
    },
    [debouncedAvailableUsersSearch]
  );

  // Handler for added users search input - memoize to create stable references
  const handleAddedUsersSearch = useCallback(
    (query) => {
      setAddedUsersSearchQuery(query);
      debouncedAddedUsersSearch(query);
    },
    [debouncedAddedUsersSearch]
  );

  // Debounced search for Geofence Listing
  const debouncedSearch = useCallback(
    debounce((query) => {
      const data = {
        ...paramsObject,
        search_query: query,
        page_number: 0,
      };
      setPageNo(0);
      setGetListParams(data);
    }, 500),
    [paramsObject, setGetListParams]
  );

  // Load more available users - for infinite scroll
  const loadMoreAvailableUsers = useCallback(() => {
    if (allUsersList?.data?.has_more) {
      const nextPage = availableUsersPageNo + 1;
      setAvailableUsersPageNo(nextPage);
      setAllUsersParams({
        branch_id: selectedBranchId,
        search_query: availableUsersSearchQuery,
        page_number: nextPage,
        limit: 10,
      });
    }
  }, [
    allUsersList?.data?.has_more,
    availableUsersPageNo,
    selectedBranchId,
    availableUsersSearchQuery,
    setAllUsersParams,
  ]);

  // Load more added users - for infinite scroll
  const loadMoreAddedUsers = useCallback(() => {
    if (mappedUsersList?.data?.has_more) {
      const nextPage = addedUsersPageNo + 1;
      setAddedUsersPageNo(nextPage);
      setMappedUsersParams({
        branch_id: selectedBranchId,
        search_query: addedUsersSearchQuery,
        page_number: nextPage,
        limit: 10,
      });
    }
  }, [
    mappedUsersList?.data?.has_more,
    addedUsersPageNo,
    selectedBranchId,
    addedUsersSearchQuery,
    setMappedUsersParams,
  ]);

  // Handle search
  const handleSearch = (query) => {
    setSearchTerm(query);
    debouncedSearch(query);
  };

  // Handle page change for pagination
  const handlePageChange = (page) => {
    const data = {
      ...paramsObject,
      page_number: page.selected,
    };
    setPageNo(page.selected);
    setGetListParams(data);
  };

  // Open edit modal with geofence data
  const handleEditLocation = (branchId) => {
    setSelectedBranchId(branchId);
    setIsEditing(true);
    setSingleGeofenceParams({ branch_id: branchId });
    setAddLocationModal(true);
  };

  // Open delete confirmation modal
  const handleOpenDeleteModal = (branchId) => {
    setSelectedBranchId(branchId);
    setDeleteConfirmationModal(true);
  };

  // Handle delete location
  const handleDeleteLocation = () => {
    setIsDeleting(true);
    deleteGeofence.mutate({ branch_id: selectedBranchId });
  };

  // Open add location modal
  const handleAddLocation = () => {
    setSelectedBranchId(null);
    setIsEditing(false);
    setAddLocationModal(true);
  };

  // Handle submit for both create and update
  const handleSubmitLocation = (formData) => {
    setIsAddingData(true);
    const payload = isEditing
      ? { ...formData, branch_id: selectedBranchId }
      : formData;
    createUpdateGeofence.mutate(payload);
  };

  // Open user management modal
  const handleOpenUserManagement = (branchId) => {
    setSelectedBranchId(branchId);
    setUserManagementModalOpen(true);

    // Reset search queries and pagination when opening modal
    setAvailableUsersSearchQuery("");
    setAddedUsersSearchQuery("");
    setAvailableUsersPageNo(0);
    setAddedUsersPageNo(0);

    // Refresh user data when opening the modal
    setAllUsersParams({
      branch_id: branchId,
      search_query: "",
      page_number: 0,
      limit: 10,
    });
    setMappedUsersParams({
      branch_id: branchId,
      search_query: "",
      page_number: 0,
      limit: 10,
    });
  };

  // Add user to the geofence (locally only)
  const handleAddUser = (user) => {
    // Move from available to added users in local state
    setLocalAvailableUsers((prev) =>
      prev.filter((u) => u.company_employee_id !== user.company_employee_id)
    );

    setLocalAddedUsers((prev) => {
      if (
        !prev.some((u) => u.company_employee_id === user.company_employee_id)
      ) {
        return [...prev, user];
      }
      return prev;
    });

    // Track for API changes
    if (
      !addedUsers.some(
        (u) => u.company_employee_id === user.company_employee_id
      )
    ) {
      const userToAdd = {
        company_employee_id: user.company_employee_id,
        user_id: user.user_id,
      };
      setAddedUsers((prev) => [...prev, userToAdd]);
    }

    // If the user was previously removed, remove from the removed list
    setRemovedUsers((prev) =>
      prev.filter((u) => u.company_employee_id !== user.company_employee_id)
    );
  };

  // Remove user from the geofence (locally only)
  const handleRemoveUser = (user) => {
    // Move from added to available users in local state
    setLocalAddedUsers((prev) =>
      prev.filter((u) => u.company_employee_id !== user.company_employee_id)
    );

    setLocalAvailableUsers((prev) => {
      if (
        !prev.some((u) => u.company_employee_id === user.company_employee_id)
      ) {
        return [...prev, user];
      }
      return prev;
    });

    // If the user was previously added, remove from the added list
    setAddedUsers((prev) =>
      prev.filter((u) => u.company_employee_id !== user.company_employee_id)
    );

    // Track for API changes - only add to removed list if not newly added
    const userToRemove = {
      company_employee_id: user.company_employee_id,
      user_id: user.user_id,
    };

    if (
      !removedUsers.some(
        (u) => u.company_employee_id === user.company_employee_id
      )
    ) {
      setRemovedUsers((prev) => [...prev, userToRemove]);
    }
  };

  // Save user mapping changes
  const handleSaveUsers = () => {
    if (!selectedBranchId) return;

    mapUsers.mutate({
      branch_id: selectedBranchId,
      added_users_id: JSON.stringify(addedUsers),
      remove_users_id: JSON.stringify(removedUsers),
    });
  };

  // Loading state
  const isLoading = geofenceList?.isLoading || geofenceList?.isFetching;

  return {
    // States
    pageNo,
    searchTerm,
    isLoading,
    isAddingData,
    isEditing,
    isSubmitting,
    isDeleting,
    isDeleteSubmitting,
    isMapping,
    addLocationModal,
    selectedBranchId,
    userManagementModalOpen,
    deleteConfirmationModal,
    availableUsersSearchQuery,
    addedUsersSearchQuery,

    // Data
    geofenceList: geofenceList?.data,
    singleGeofenceData: singleGeofenceData?.data?.data,
    isSingleGeofenceLoading: singleGeofenceData?.isLoading,
    limit: paramsObject.limit,
    availableUsers: localAvailableUsers,
    addedUsers: localAddedUsers,
    isUsersLoading: allUsersList?.isLoading || mappedUsersList?.isLoading,
    isAvailableUsersLoading: allUsersList?.isLoading,
    isAddedUsersLoading: mappedUsersList?.isLoading,
    hasMoreAvailableUsers: allUsersList?.data?.has_more,
    hasMoreAddedUsers: mappedUsersList?.data?.has_more,
    isFetchingAvailableUsers: allUsersList?.isFetching,
    isFetchingAddedUsers: mappedUsersList?.isFetching,

    // Functions
    handleSearch,
    handlePageChange,
    handleEditLocation,
    handleOpenDeleteModal,
    handleDeleteLocation,
    handleAddLocation,
    handleSubmitLocation,
    handleOpenUserManagement,
    handleAddUser,
    handleRemoveUser,
    handleSaveUsers,
    handleAvailableUsersSearch,
    handleAddedUsersSearch,
    loadMoreAvailableUsers,
    loadMoreAddedUsers,
    setAddLocationModal,
    setUserManagementModalOpen,
    setDeleteConfirmationModal,
  };
};

export default useGeofencing;
